import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { DateTimePicker, PickerCalendar } from '@/components/common/datePicker';
import { formatDateOnly, formatDayKo, initialMonth } from '@/components/common/datePicker/pickerUtils';
import { PickerBody } from '@/components/common/datePicker/pickerStyles';
import { api } from '@/api/repository/axios';
import type { SubwayStation } from '@/api/repository/types/types';
import {
    FilterChip,
    FilterChipBar,
    FilterPanel,
    OptionChip,
    OptionGroup,
    filterTokens,
    summarizeSelection,
    useFilterSave,
    useLatestCount,
} from '@/components/common/filter';
import type { FilterPanelControls, FilterSaveControls } from '@/components/common/filter';
import { StationResultList, useStationSearch } from '@/components/common/station/stationSearch';
import type { StationSearchState } from '@/components/common/station/stationSearch';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import {
    WORKDAY_OPTIONS,
    WORK_PERIOD_OPTIONS,
    countRecruitmentConditions,
    namesOf,
    parseSavedRecruitmentQuery,
    recruitmentQueryKey,
    savedRecruitmentCompareKey,
    savedRecruitmentParams,
    savedRecruitmentQueryString,
    toRecruitmentApiRequest,
    toggleInOrder,
    withWorkDates,
    withWorkPeriods,
    workDateName,
    PAY_TIMING_FILTER_OPTIONS,
    SAME_DAY_PAY_TIMING,
    toggleSameDayPay,
} from '@/pages/Recruitments/recruitmentListQuery';
import type { RecruitmentApiRequest, RecruitmentListQuery, RecruitmentOption } from '@/pages/Recruitments/recruitmentListQuery';
import {
    cityAllKey,
    isGroupPicked,
    pickCountByCity,
    regionChipLabel,
    regionPicks,
    toggleCityAll,
    toggleDistrict,
    toggleGroup,
} from '@/pages/Recruitments/recruitmentRegions';
import type { CityRegions, RegionData, RegionDistrict, RegionGroup, RegionPick } from '@/pages/Recruitments/recruitmentRegions';
import { knownStation, rememberStations, stationChipLabel, stationPickLabel } from '@/pages/Recruitments/recruitmentStations';

/**
 * 채용 목록 조건 칩 줄 — [필터 N] [지역 | 지하철역] [직무] [근무 요일] [근무 날짜] [근무형태] [경력] [복지] [급구]
 * 칩을 누르면 폰은 아래 시트, PC 는 칩 아래 팝업(공용 FilterPanel — 세미나 목록과 같은 모양).
 * 고른 것은 [공고 N개 보기]를 눌러야 적용된다(N = 같은 조건으로 서버가 센 전체 수). [급구]는 누르면 바로 켜고 끈다.
 */

export type RecruitmentPanelKind = 'all' | 'job' | 'days' | 'dates' | 'type' | 'career' | 'welfare' | 'region' | 'subway';

interface OpenPanel {
    kind: RecruitmentPanelKind;
    anchor: HTMLElement;
}

/** 시트·팝업 선택지(서버 /recruitments/init 메타) */
export interface RecruitmentFilterOptions {
    jobTasks: RecruitmentOption[];
    recruitmentTypes: RecruitmentOption[];
    careers: RecruitmentOption[];
    welfares: RecruitmentOption[];
}

/** 근무 날짜 조건 안내 — 날짜는 단기 공고(기간 근무·특정 날짜)에만 걸린다 */
const WORK_DATES_NOTE: string = '기간 근무·특정 날짜 공고에서 찾아요';

const PANEL_TITLES: Record<RecruitmentPanelKind, string> = {
    all: '필터',
    job: '직무',
    days: '근무 요일',
    dates: '근무 날짜',
    type: '근무형태',
    career: '경력',
    welfare: '복지',
    region: '지역',
    subway: '지하철역',
};

const PANEL_WIDTHS: Record<RecruitmentPanelKind, number> = {
    all: 460,
    job: 420,
    days: 380,
    dates: 360,
    type: 380,
    career: 380,
    welfare: 460,
    region: 640,
    subway: 440,
};

/** 지역·역 팝업(PC) 높이 — 안쪽 칸만 스크롤 */
const FILL_POPOVER_HEIGHT_PX: number = 520;
/** 지역·역 시트(폰) 높이 */
const FILL_SHEET_HEIGHT: string = '88dvh';
/** 왼쪽 "전체"(전국) 칸 id */
const NATIONWIDE_TAB: string = '0';
/** 지역을 하나도 안 골랐을 때 먼저 보일 시·도(서울) */
const DEFAULT_CITY_ID: string = '1100000000';

const { color } = filterTokens;

// ───────── 지역 고르기 ─────────

const RegionCols = styled.div<{ pc: boolean }>`
    display: grid;
    grid-template-columns: ${({ pc }) => (pc ? '120px' : '92px')} minmax(0, 1fr);
    gap: 10px;
    flex: 1;
    min-height: 0;
    margin-top: 12px;
`;

const CityList = styled.div`
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 4px 0;
    border-radius: 12px;
    background: ${FORM_COLOR.bg};
`;

const CityButton = styled.button<{ active: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    width: 100%;
    min-height: 44px;
    padding: 0 10px 0 12px;
    border: none;
    background: ${({ active }) => (active ? color.surface : 'transparent')};
    box-shadow: ${({ active }) => (active ? `inset 3px 0 0 ${color.accent}` : 'none')};
    color: ${({ active }) => (active ? color.selectedText : color.textSub)};
    font-family: inherit;
    font-size: 15px;
    font-weight: ${({ active }) => (active ? 800 : 500)};
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:focus-visible {
        outline: 2px solid ${color.accent};
        outline-offset: -2px;
    }
`;

const CountBadge = styled.span`
    flex-shrink: 0;
    min-width: 18px;
    padding: 0 6px;
    border-radius: 9px;
    background: ${color.accent};
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    box-sizing: border-box;
`;

const RegionPane = styled.div`
    min-width: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0 2px 12px 0;
`;

const CellGrid = styled.div<{ columns: number }>`
    display: grid;
    grid-template-columns: repeat(${({ columns }) => columns}, minmax(0, 1fr));
    gap: 6px;

    & + & {
        margin-top: 6px;
    }
`;

const Cell = styled.button<{ selected: boolean; full?: boolean }>`
    grid-column: ${({ full }) => (full ? '1 / -1' : 'auto')};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    min-width: 0;
    height: 44px;
    padding: 0 10px 0 12px;
    border: 1px solid ${({ selected }) => (selected ? color.accent : color.divider)};
    border-radius: 10px;
    background: ${({ selected }) => (selected ? color.selectedBg : color.surface)};
    color: ${({ selected }) => (selected ? color.selectedText : color.text)};
    font-family: inherit;
    font-size: 14.5px;
    font-weight: ${({ selected }) => (selected ? 700 : 500)};
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    svg {
        flex-shrink: 0;
        font-size: 18px;
    }

    &:focus-visible {
        outline: 2px solid ${color.accent};
        outline-offset: 1px;
    }
`;

const SubTitle = styled.div`
    margin: 16px 0 8px;
    font-size: 13px;
    font-weight: 700;
    color: ${color.textMuted};
`;

const Tray = styled.div`
    flex-shrink: 0;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid ${FORM_COLOR.lineSoft};
`;

const TrayTitle = styled.div`
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 700;
    color: ${color.textMuted};
`;

const TrayScroller = styled.div`
    display: flex;
    gap: 6px;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const TrayChip = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    padding: 0 8px 0 11px;
    border: 1px solid ${color.accent};
    border-radius: 16px;
    background: ${color.selectedBg};
    color: ${color.selectedText};
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;

    svg {
        font-size: 16px;
    }
`;

const TrayEmpty = styled.div`
    font-size: 13px;
    color: ${color.textMuted};
    line-height: 32px;
`;

const Loading = styled.p`
    margin: 24px 0;
    text-align: center;
    font-size: 14px;
    color: ${color.textMuted};
`;

interface RegionPickerProps {
    data: RegionData | null;
    selected: string[];
    pc: boolean;
    onChange: (next: string[]) => void;
}

/** 왼쪽 시·도(고른 개수 숫자) | 오른쪽 같은 폭 칸 목록(행정구역 코드 순) + 아래 "고른 지역 N" */
const RegionPicker = ({ data, selected, pc, onChange }: RegionPickerProps) => {
    // 처음 열 때 고른 지역이 있는 시·도, 없으면 서울. 사용자가 시·도를 누르면 그 뒤로는 그 시·도
    const [initialCity] = useState<string | undefined>(() => selected[0]?.split('_')[0]);
    const [chosenCity, setChosenCity] = useState<string | null>(null);
    const defaultCity: string = initialCity && data?.byCity[initialCity]
        ? initialCity
        : data?.byCity[DEFAULT_CITY_ID] ? DEFAULT_CITY_ID : (data?.cities[0]?.id ?? NATIONWIDE_TAB);
    const activeCity: string = chosenCity ?? defaultCity;
    const paneRef = useRef<HTMLDivElement>(null);

    const picks: RegionPick[] = regionPicks(selected, data);
    const counts: Record<string, number> = pickCountByCity(selected, data);
    const columns: number = pc ? 4 : 2;

    const selectCity = (cityId: string): void => {
        setChosenCity(cityId);
        if (paneRef.current) paneRef.current.scrollTop = 0;
    };

    const cell = (key: string, label: string, isSelected: boolean, onClick: () => void, full: boolean = false): React.ReactNode => (
        <Cell key={key} type="button" selected={isSelected} full={full} aria-pressed={isSelected} onClick={onClick}>
            <span>{label}</span>
            {isSelected && <CheckRoundedIcon aria-hidden="true" />}
        </Cell>
    );

    const renderCity = (cityRegions: CityRegions): React.ReactNode => {
        const cityId: string = cityRegions.city.id;
        const cityAll: boolean = selected.includes(cityAllKey(cityId));
        const districtCell = (district: RegionDistrict): React.ReactNode =>
            cell(district.key, district.shortName, selected.includes(district.key), () => data && onChange(toggleDistrict(selected, district, data)));
        return (
            <>
                <CellGrid columns={columns}>
                    {cell(cityAllKey(cityId), `${cityRegions.city.name} 전체`, cityAll, () => onChange(toggleCityAll(selected, cityId)), true)}
                </CellGrid>
                {cityRegions.groups.length > 0 && <SubTitle>시·군</SubTitle>}
                {cityRegions.singles.length > 0 && (
                    <CellGrid columns={columns} style={cityRegions.groups.length > 0 ? undefined : { marginTop: 6 }}>
                        {cityRegions.singles.map(districtCell)}
                    </CellGrid>
                )}
                {cityRegions.groups.map((group: RegionGroup) => (
                    <React.Fragment key={group.name}>
                        <SubTitle>{group.name}</SubTitle>
                        <CellGrid columns={columns}>
                            {cell(`group:${group.name}`, `${group.name} 전체`, isGroupPicked(group, selected), () => onChange(toggleGroup(selected, group, cityId)), true)}
                            {group.children.map(districtCell)}
                        </CellGrid>
                    </React.Fragment>
                ))}
            </>
        );
    };

    const activeRegions: CityRegions | undefined = data?.byCity[activeCity];
    return (
        <>
            <RegionCols pc={pc}>
                <CityList role="tablist" aria-label="시·도" aria-orientation="vertical">
                    <CityButton type="button" role="tab" aria-selected={activeCity === NATIONWIDE_TAB} active={activeCity === NATIONWIDE_TAB} onClick={() => selectCity(NATIONWIDE_TAB)}>
                        전체
                    </CityButton>
                    {(data?.cities ?? []).map((city) => (
                        <CityButton key={city.id} type="button" role="tab" aria-selected={activeCity === city.id} active={activeCity === city.id} onClick={() => selectCity(city.id)}>
                            {city.name}
                            {(counts[city.id] ?? 0) > 0 && <CountBadge aria-label={`${counts[city.id]}곳 고름`}>{counts[city.id]}</CountBadge>}
                        </CityButton>
                    ))}
                </CityList>
                <RegionPane ref={paneRef} role="tabpanel" aria-label={activeRegions ? `${activeRegions.city.name} 구·군` : '전국'}>
                    {!data ? (
                        <Loading>지역을 불러오는 중이에요…</Loading>
                    ) : activeRegions ? (
                        renderCity(activeRegions)
                    ) : (
                        <CellGrid columns={columns}>{cell('nationwide', '전국', selected.length === 0, () => onChange([]), true)}</CellGrid>
                    )}
                </RegionPane>
            </RegionCols>
            <Tray>
                <TrayTitle>고른 지역 {picks.length}</TrayTitle>
                {picks.length === 0 ? (
                    <TrayEmpty>전국</TrayEmpty>
                ) : (
                    <TrayScroller data-no-swipe-back="">
                        {picks.map((pick: RegionPick) => (
                            <TrayChip key={pick.id} type="button" aria-label={`${pick.label} 빼기`} onClick={() => onChange(selected.filter((key: string) => !pick.keys.includes(key)))}>
                                {pick.label}
                                <CloseRoundedIcon aria-hidden="true" />
                            </TrayChip>
                        ))}
                    </TrayScroller>
                )}
            </Tray>
        </>
    );
};

// ───────── 지하철역 고르기 ─────────

const SearchInput = styled.input`
    flex-shrink: 0;
    width: 100%;
    height: 48px;
    margin-top: 12px;
    padding: 0 14px;
    border: 1px solid ${FORM_COLOR.border};
    border-radius: 12px;
    font-size: 16px; /* 16px 미만이면 iOS 가 입력칸을 확대한다 */
    font-family: inherit;
    color: ${FORM_COLOR.text};
    background: #fff;
    outline: none;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;

    &:focus {
        border-color: ${FORM_COLOR.primary};
    }

    &::placeholder {
        color: ${FORM_COLOR.placeholder};
    }

    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
        -webkit-appearance: none;
        appearance: none;
    }
`;

const StationResults = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    margin-top: 6px;
`;

interface StationPickerProps {
    selected: string[];
    pc: boolean;
    onChange: (next: string[]) => void;
}

/** 역 이름 검색(세미나 등록과 같은 역 검색) + 고른 역 칩 */
const StationPicker = ({ selected, pc, onChange }: StationPickerProps) => {
    const [text, setText] = useState<string>('');
    const search: StationSearchState = useStationSearch(text);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectedIds: Set<string> = useMemo(() => new Set(selected), [selected]);

    // PC 는 바로 입력(폰은 키보드가 시트를 덮지 않게 눌렀을 때만)
    useEffect(() => {
        if (!pc) return undefined;
        const timer: number = window.setTimeout(() => inputRef.current?.focus(), 60);
        return () => window.clearTimeout(timer);
    }, [pc]);

    const toggle = (station: SubwayStation): void => {
        rememberStations([station]);
        onChange(selectedIds.has(station.id) ? selected.filter((id: string) => id !== station.id) : [...selected, station.id]);
    };

    return (
        <>
            <SearchInput
                ref={inputRef}
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                placeholder="역 이름 (예: 강남, 서면)"
                aria-label="역 이름 검색"
                value={text}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setText(event.target.value)}
            />
            <StationResults aria-live="polite">
                <StationResultList search={search} selectedIds={selectedIds} onToggle={toggle} />
            </StationResults>
            <Tray>
                <TrayTitle>고른 역 {selected.length}</TrayTitle>
                {selected.length === 0 ? (
                    <TrayEmpty>역을 고르지 않으면 전국 공고를 보여 줘요</TrayEmpty>
                ) : (
                    <TrayScroller data-no-swipe-back="">
                        {selected.map((id: string) => {
                            const station: SubwayStation | undefined = knownStation(id);
                            const label: string = station ? stationPickLabel(station) : '역';
                            return (
                                <TrayChip key={id} type="button" aria-label={`${label} 빼기`} onClick={() => onChange(selected.filter((item: string) => item !== id))}>
                                    {label}
                                    <CloseRoundedIcon aria-hidden="true" />
                                </TrayChip>
                            );
                        })}
                    </TrayScroller>
                )}
            </Tray>
        </>
    );
};

const WorkDatesNote = styled.p`
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.5;
    color: ${color.textMuted};
`;

const PickedDays = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-height: 36px;
    margin-top: 10px;
`;

// ───────── 창(초안·결과 수) ─────────

interface RecruitmentFilterPanelProps {
    kind: RecruitmentPanelKind;
    anchor: HTMLElement;
    query: RecruitmentListQuery;
    options: RecruitmentFilterOptions;
    regionData: RegionData | null;
    isMobile: boolean;
    onClose: () => void;
    onApply: (next: RecruitmentListQuery) => void;
}

/** 열려 있는 동안만 그려진다 — 고르던 상태(초안)와 결과 수 세기는 창이 열릴 때 시작해 닫히면 버린다 */
const RecruitmentFilterPanel = ({ kind, anchor, query, options, regionData, isMobile, onClose, onApply }: RecruitmentFilterPanelProps) => {
    const [draft, setDraftState] = useState<RecruitmentListQuery>(query);
    const draftRef = useRef<RecruitmentListQuery>(query);
    const setDraft = (next: RecruitmentListQuery): void => {
        draftRef.current = next;
        setDraftState(next);
    };

    // 검색어는 다른 조건과 함께 못 쓴다(검색 API 가 따로) — 창에서 고르는 순간부터 검색어 없는 목록으로 센다
    const countKey: string = recruitmentQueryKey({ ...draft, search: '' });
    const fetchCount = useCallback(async (): Promise<number> => {
        const request: RecruitmentApiRequest = toRecruitmentApiRequest({ ...draftRef.current, search: '' });
        const response = await api.recruitments.listPage(request.kind, request.params, 1, 0);
        if (response.code !== 0) throw new Error('count failed');
        return Number(response.result?.total ?? 0);
    }, []);
    const { count } = useLatestCount(fetchCount, countKey);

    /** 오늘(yyyy-MM-dd) — 지난 날짜는 고를 수 없다 */
    const today: string = useMemo(() => formatDateOnly(new Date()), []);
    /** [근무 날짜] 창 달력이 보여 주는 달 — 고른 첫 날(없으면 이번 달) */
    const [month, setMonth] = useState<Date>(() => initialMonth(query.workDates[0], formatDateOnly(new Date())));

    /** 이 조건 창을 닫으며 적용하는 길 — 저장한 뒤 그 조건을 목록에 바로 적용할 때 쓴다 */
    const panelControlsRef = useRef<FilterPanelControls | null>(null);
    // 조건 저장(모든 창의 제목 줄) — 어느 창에서 누르든 지금 조건 전체(탭·고른 지역·고른 역·나머지 조건)를 저장한다.
    // 지난 근무 날짜는 저장한 조건에서 빼고 견준다
    const daysNames: string[] = [...namesOf(draft.workdays, WORKDAY_OPTIONS), ...namesOf(draft.periods, WORK_PERIOD_OPTIONS)];
    const saveSummary: string[] = [
        // 위치 — 고른 지역과 고른 역을 둘 다(알림은 둘 중 하나라도 맞으면 간다). 둘 다 없으면 전국
        ...(draft.districts.length > 0 ? [regionChipLabel(draft.districts, regionData)] : []),
        ...(draft.stations.length > 0 ? [`지하철역 ${stationChipLabel(draft.stations)}`] : []),
        ...(draft.districts.length === 0 && draft.stations.length === 0 ? ['전국'] : []),
        ...[
            namesOf(draft.jobTasks, options.jobTasks),
            daysNames,
            draft.workDates.map(workDateName),
            namesOf(draft.recruitmentTypes, options.recruitmentTypes),
            namesOf(draft.careers, options.careers),
            namesOf(draft.welfares, options.welfares),
        ].filter((names: string[]) => names.length > 0).map((names: string[]) => summarizeSelection(names, '')),
        ...(draft.emergency ? ['급구만'] : []),
        ...namesOf(draft.payTimings, PAY_TIMING_FILTER_OPTIONS),
    ];
    const filterSave: FilterSaveControls = useFilterSave({
        domain: 'recruitment',
        draftQuery: savedRecruitmentQueryString(draft),
        // 탭은 견주지 않는다 — 저장해 둔 채 지역별·지하철역별 탭만 바꿔도 [저장됨]
        isSameAsSaved: (savedQuery: string) => savedRecruitmentCompareKey(parseSavedRecruitmentQuery(savedQuery, today)) === savedRecruitmentCompareKey(draft),
        getParams: () => savedRecruitmentParams(draftRef.current),
        summary: saveSummary,
        onSaved: () => panelControlsRef.current?.apply(),
        alertTarget: '채용공고',
    });

    const applyLabel: string = count === null
        ? '공고 보기'
        : count === 0
            ? '조건에 맞는 공고가 없어요'
            : `공고 ${count.toLocaleString()}개 보기`;

    const reset = (): void => {
        switch (kind) {
            case 'job': setDraft({ ...draft, jobTasks: [] }); break;
            case 'days': setDraft({ ...draft, workdays: [], periods: [], workDates: [] }); break;
            case 'dates': setDraft({ ...draft, workDates: [] }); break;
            case 'type': setDraft({ ...draft, recruitmentTypes: [] }); break;
            case 'career': setDraft({ ...draft, careers: [] }); break;
            case 'welfare': setDraft({ ...draft, welfares: [] }); break;
            case 'region': setDraft({ ...draft, districts: [] }); break;
            case 'subway': setDraft({ ...draft, stations: [] }); break;
            default:
                setDraft({ ...draft, jobTasks: [], workdays: [], periods: [], workDates: [], recruitmentTypes: [], careers: [], welfares: [], emergency: false, payTimings: [] });
        }
    };

    const optionGroup = (label: string | undefined, list: RecruitmentOption[], values: string[], set: (next: string[]) => void, strong: boolean = Boolean(label)): React.ReactNode => {
        const order: string[] = list.map((option: RecruitmentOption) => option.value);
        return (
            <OptionGroup label={label} strong={strong}>
                {list.map((option: RecruitmentOption) => (
                    <OptionChip key={option.value} selected={values.includes(option.value)} onClick={() => set(toggleInOrder(values, option.value, order))}>
                        {option.name}
                    </OptionChip>
                ))}
            </OptionGroup>
        );
    };

    const jobView = (label?: string): React.ReactNode => optionGroup(label, options.jobTasks, draft.jobTasks, (jobTasks: string[]) => setDraft({ ...draft, jobTasks }));
    const typeView = (label?: string): React.ReactNode => optionGroup(label, options.recruitmentTypes, draft.recruitmentTypes, (recruitmentTypes: string[]) => setDraft({ ...draft, recruitmentTypes }));
    const careerView = (label?: string): React.ReactNode => optionGroup(label, options.careers, draft.careers, (careers: string[]) => setDraft({ ...draft, careers }));
    const welfareView = (label?: string): React.ReactNode => optionGroup(label, options.welfares, draft.welfares, (welfares: string[]) => setDraft({ ...draft, welfares }));
    /** 근무 요일 + 근무 기간(2026-09-30 사용자 확정 — 요일 중심). 전체 필터에서는 두 섹션, 빠른 칩 창에서는 소제목 두 묶음 */
    const daysView = (strongTitle: boolean): React.ReactNode => (
        <>
            {optionGroup(strongTitle ? '근무 요일' : '요일', WORKDAY_OPTIONS, draft.workdays, (workdays: string[]) => setDraft({ ...draft, workdays }), strongTitle)}
            {optionGroup(strongTitle ? '근무 기간' : '기간', WORK_PERIOD_OPTIONS, draft.periods, (periods: string[]) => setDraft(withWorkPeriods(draft, periods)), strongTitle)}
            {/* 근무 날짜 — 고른 날 칩(누르면 빠짐) + 날짜 고르기(공용 달력, 여러 날). 창 위에 달력이 한 겹 더 뜬다 */}
            <OptionGroup label="근무 날짜" strong={strongTitle}>
                {draft.workDates.map((day: string) => (
                    <OptionChip key={day} selected ariaLabel={`${formatDayKo(day)} 빼기`} onClick={() => setDraft(withWorkDates(draft, draft.workDates.filter((item: string) => item !== day)))}>
                        {workDateName(day)}
                    </OptionChip>
                ))}
                <DateTimePicker
                    mode="dates"
                    title="근무 날짜"
                    placeholder={draft.workDates.length > 0 ? '＋ 날짜 추가' : '＋ 날짜 고르기'}
                    value={draft.workDates}
                    onChange={(days: string[]) => setDraft(withWorkDates(draft, days))}
                    minDate={today}
                    inModal
                />
            </OptionGroup>
            <WorkDatesNote>{WORK_DATES_NOTE}</WorkDatesNote>
        </>
    );

    /** [근무 날짜] 칩 창 — 달력이 바로 보이고 누르는 대로 고르거나 뺀다(여러 날). 고른 날은 아래 칩으로도 뺄 수 있다 */
    const datesView = (): React.ReactNode => {
        const toggleDay = (day: string): void =>
            setDraft(withWorkDates(draft, draft.workDates.includes(day) ? draft.workDates.filter((item: string) => item !== day) : [...draft.workDates, day]));
        return (
            <PickerBody data-no-swipe-back="">
                <PickerCalendar
                    month={month}
                    onMonthChange={setMonth}
                    isPicked={(day: string) => draft.workDates.includes(day)}
                    isDisabled={(day: string) => day < today}
                    onPick={toggleDay}
                />
                <PickedDays aria-live="polite">
                    {draft.workDates.map((day: string) => (
                        <OptionChip key={day} selected ariaLabel={`${formatDayKo(day)} 빼기`} onClick={() => toggleDay(day)}>
                            {workDateName(day)}
                        </OptionChip>
                    ))}
                </PickedDays>
                {/* 제목 줄에는 저장 버튼이 있어 안내는 달력 아래에 둔다 */}
                <WorkDatesNote>{WORK_DATES_NOTE}</WorkDatesNote>
            </PickerBody>
        );
    };

    const renderBody = (): React.ReactNode => {
        switch (kind) {
            case 'job': return <div style={{ paddingTop: 12 }}>{jobView()}</div>;
            case 'days': return <div style={{ paddingTop: 2 }}>{daysView(false)}</div>;
            case 'dates': return datesView();
            case 'type': return <div style={{ paddingTop: 12 }}>{typeView()}</div>;
            case 'career': return <div style={{ paddingTop: 12 }}>{careerView()}</div>;
            case 'welfare': return <div style={{ paddingTop: 12 }}>{welfareView()}</div>;
            case 'region':
                return <RegionPicker data={regionData} selected={draft.districts} pc={!isMobile} onChange={(districts: string[]) => setDraft({ ...draft, districts })} />;
            case 'subway':
                return <StationPicker selected={draft.stations} pc={!isMobile} onChange={(stations: string[]) => setDraft({ ...draft, stations })} />;
            default:
                return (
                    <>
                        {jobView('직무')}
                        {daysView(true)}
                        {typeView('근무형태')}
                        {/* 급여 지급 시점(당일 지급) — 근무형태(알바·단기) 바로 아래, 급여 성격의 조건 */}
                        {optionGroup('급여', PAY_TIMING_FILTER_OPTIONS, draft.payTimings, (payTimings: string[]) => setDraft({ ...draft, payTimings }))}
                        {careerView('경력')}
                        {welfareView('복지')}
                        <OptionGroup label="급구" strong>
                            <OptionChip selected={draft.emergency} onClick={() => setDraft({ ...draft, emergency: !draft.emergency })}>급구만</OptionChip>
                        </OptionGroup>
                    </>
                );
        }
    };

    const fill: boolean = kind === 'region' || kind === 'subway';
    return (
        <>
            <FilterPanel
                open
                anchorEl={anchor}
                title={PANEL_TITLES[kind]}
                headerAction={filterSave.headerAction}
                popoverWidth={PANEL_WIDTHS[kind]}
                fillBody={fill}
                popoverHeight={fill ? FILL_POPOVER_HEIGHT_PX : undefined}
                sheetMaxHeight={fill ? FILL_SHEET_HEIGHT : undefined}
                onClose={onClose}
                onApply={() => onApply(draftRef.current)}
                onReset={reset}
                applyLabel={applyLabel}
                applyDisabled={count === 0}
            >
                {(controls: FilterPanelControls) => {
                    panelControlsRef.current = controls;
                    return renderBody();
                }}
            </FilterPanel>
            {filterSave.savePanel}
        </>
    );
};

// ───────── 칩 줄 ─────────

export interface RecruitmentFilterBarProps {
    query: RecruitmentListQuery;
    options: RecruitmentFilterOptions;
    regionData: RegionData | null;
    isMobile: boolean;
    onApply: (next: RecruitmentListQuery) => void;
    /** 정렬(SortSelect) — PC 만(줄 오른쪽 끝). 폰 정렬은 화면이 본문 맨 위 조작 줄에 따로 그린다 */
    sortChip?: React.ReactNode;
    /** 주소로 들어오며 바로 열 창(홈 "직무별" 바로가기 → 직무) — 한 번 열고 [onAutoOpened] */
    autoOpen?: RecruitmentPanelKind | null;
    onAutoOpened?: () => void;
    className?: string;
}

const RecruitmentFilterBar = ({ query, options, regionData, isMobile, onApply, sortChip, autoOpen, onAutoOpened, className }: RecruitmentFilterBarProps) => {
    const [panel, setPanel] = useState<OpenPanel | null>(null);
    const chipRefs = useRef<Partial<Record<RecruitmentPanelKind, HTMLButtonElement | null>>>({});

    useEffect(() => {
        if (!autoOpen) return;
        const anchor: HTMLButtonElement | null | undefined = chipRefs.current[autoOpen];
        if (!anchor) return;
        setPanel({ kind: autoOpen, anchor });
        onAutoOpened?.();
    }, [autoOpen, onAutoOpened]);

    const toggle = (kind: RecruitmentPanelKind) => (event: React.MouseEvent<HTMLButtonElement>): void => {
        const anchor: HTMLElement = event.currentTarget;
        setPanel((previous: OpenPanel | null) => (previous?.kind === kind ? null : { kind, anchor }));
    };
    const close = (): void => setPanel(null);
    const apply = (next: RecruitmentListQuery): void => {
        setPanel(null);
        // 조건을 바꾸면 검색어는 푼다(검색 API 는 다른 조건과 함께 못 쓴다)
        const changed: boolean = recruitmentQueryKey({ ...next, search: '' }) !== recruitmentQueryKey({ ...query, search: '' });
        onApply(changed ? { ...next, search: '' } : next);
    };

    const isOpen = (kind: RecruitmentPanelKind): boolean => panel?.kind === kind;
    const setChipRef = (kind: RecruitmentPanelKind) => (element: HTMLButtonElement | null): void => { chipRefs.current[kind] = element; };
    const conditionCount: number = countRecruitmentConditions(query);
    const daysNames: string[] = [...namesOf(query.workdays, WORKDAY_OPTIONS), ...namesOf(query.periods, WORK_PERIOD_OPTIONS)];
    /** [근무 날짜] 칩 글 — "10/14", 여러 날이면 "10/14 외 1일" */
    const datesLabel: string = query.workDates.length === 0
        ? '근무 날짜'
        : `${workDateName(query.workDates[0])}${query.workDates.length > 1 ? ` 외 ${query.workDates.length - 1}일` : ''}`;
    const locationKind: RecruitmentPanelKind = query.tab === 'subway' ? 'subway' : 'region';
    const locationSelected: boolean = query.tab === 'subway' ? query.stations.length > 0 : query.districts.length > 0;
    const locationLabel: string = query.tab === 'subway' ? stationChipLabel(query.stations) : regionChipLabel(query.districts, regionData);

    const quick = (kind: RecruitmentPanelKind, values: string[], names: string[], placeholder: string): React.ReactNode => (
        <FilterChip
            label={summarizeSelection(names, placeholder)}
            selected={values.length > 0}
            hasPanel
            open={isOpen(kind)}
            buttonRef={setChipRef(kind)}
            onClick={toggle(kind)}
        />
    );

    return (
        <>
            <FilterChipBar ariaLabel="채용 조건" className={className} end={isMobile ? undefined : sortChip}>
                <FilterChip
                    label="필터"
                    ariaLabel={conditionCount > 0 ? `필터 ${conditionCount}개 적용됨` : '필터'}
                    icon={<TuneRoundedIcon aria-hidden="true" />}
                    badge={conditionCount}
                    selected={conditionCount > 0}
                    hasPanel
                    arrow={false}
                    open={isOpen('all')}
                    buttonRef={setChipRef('all')}
                    onClick={toggle('all')}
                />
                <FilterChip
                    label={locationLabel}
                    ariaLabel={`${query.tab === 'subway' ? '지하철역' : '지역'}: ${locationLabel}`}
                    selected={locationSelected}
                    hasPanel
                    open={isOpen(locationKind)}
                    buttonRef={setChipRef(locationKind)}
                    onClick={toggle(locationKind)}
                />
                {quick('job', query.jobTasks, namesOf(query.jobTasks, options.jobTasks), '직무')}
                {quick('days', [...query.workdays, ...query.periods], daysNames, '근무 요일')}
                <FilterChip
                    label={datesLabel}
                    ariaLabel={query.workDates.length > 0 ? `근무 날짜: ${query.workDates.map(workDateName).join(', ')}` : '근무 날짜'}
                    selected={query.workDates.length > 0}
                    hasPanel
                    open={isOpen('dates')}
                    buttonRef={setChipRef('dates')}
                    onClick={toggle('dates')}
                />
                {quick('type', query.recruitmentTypes, namesOf(query.recruitmentTypes, options.recruitmentTypes), '근무형태')}
                {quick('career', query.careers, namesOf(query.careers, options.careers), '경력')}
                {quick('welfare', query.welfares, namesOf(query.welfares, options.welfares), '복지')}
                {/* 폰은 급구·정렬을 본문 맨 위 조작 줄(ListControlRow)에 둔다 — 칩 줄에는 PC 만 */}
                {!isMobile && (
                    <FilterChip
                        label="급구"
                        toggle
                        selected={query.emergency}
                        onClick={() => apply({ ...query, emergency: !query.emergency })}
                    />
                )}
                {/* 당일 지급 — 급구처럼 바로 켜고 끄는 조건이라 그 옆(폰은 조작 줄) */}
                {!isMobile && (
                    <FilterChip
                        label="당일 지급"
                        toggle
                        selected={query.payTimings.includes(SAME_DAY_PAY_TIMING)}
                        onClick={() => apply(toggleSameDayPay(query))}
                    />
                )}
            </FilterChipBar>
            {panel && (
                <RecruitmentFilterPanel
                    key={panel.kind}
                    kind={panel.kind}
                    anchor={panel.anchor}
                    query={query}
                    options={options}
                    regionData={regionData}
                    isMobile={isMobile}
                    onClose={close}
                    onApply={apply}
                />
            )}
        </>
    );
};

export default RecruitmentFilterBar;
